import { useCallback, useMemo, useState } from 'react';
import type { ConversationId, Message } from '@cyphex/types';
/**
 * Deep imports into `features/chat`, the direction this codebase already runs
 * in (`VoicemailComposer`, `useLeaveVoicemail`, and chat's own deep import of
 * `usePlaceCall` going the other way). The rail mounts the REAL thread, so it
 * reuses the real thread's hooks rather than standing up a second store; the
 * barrel is avoided because it would drag the chat PAGES into the call chunk.
 */
import { Composer } from '@/features/chat/components/composer/Composer';
import { QuickReactionsDialog } from '@/features/chat/components/dialogs/QuickReactionsDialog';
import { MessageTimeline } from '@/features/chat/components/thread/MessageTimeline';
import {
  ChatRequestBar,
  FailedSendBar,
  OfflineBanner,
} from '@/features/chat/components/thread/ThreadBanners';
import { TypingRow } from '@/features/chat/components/thread/TypingRow';
import { useAttachments } from '@/features/chat/hooks/useAttachments';
import { useChatRealtime } from '@/features/chat/hooks/useChatRealtime';
import { useConversation } from '@/features/chat/hooks/useConversation';
import { useMarkRead } from '@/features/chat/hooks/useMarkRead';
import { useMe } from '@/features/chat/hooks/useMe';
import { useMessageActions } from '@/features/chat/hooks/useMessageActions';
import { useMessageExtras } from '@/features/chat/hooks/useMessageExtras';
import { useOutbox } from '@/features/chat/hooks/useOutbox';
import { useOutboxFlush } from '@/features/chat/hooks/useOutboxFlush';
import { useSendMessage } from '@/features/chat/hooks/useSendMessage';
import { useThreadComposer } from '@/features/chat/hooks/useThreadComposer';
import { useTimeline } from '@/features/chat/hooks/useTimeline';
import { useTimelineRows } from '@/features/chat/hooks/useTimelineRows';
import { useTyping } from '@/features/chat/hooks/useTyping';
import { useVoiceSend } from '@/features/chat/hooks/useVoiceSend';
import { viewOf } from '@/features/chat/lib/conversationView';
import { CallForwardDialog } from './CallForwardDialog';

export interface CallChatThreadProps {
  conversationId: ConversationId;
}

/**
 * The conversation itself, in the call's 320px rail.
 *
 * Composed one level BELOW `ThreadPane` rather than reusing it: `SidePanel`
 * already owns the rail's title and close button, so ThreadPane's
 * `ConversationHeader` would be a second header for the same thread, and its
 * five large header buttons leave no room for a name at this width.
 *
 * There is one thread and one read cursor, so everything typed here lands in
 * normal chat history and everything read here is read — which is the whole
 * point of pointing the panel at `call.conversationId`.
 */
export function CallChatThread({ conversationId }: CallChatThreadProps) {
  const [atBottom, setAtBottom] = useState(true);
  const [forwarding, setForwarding] = useState<Message | null>(null);
  const [quickReactionsOpen, setQuickReactionsOpen] = useState(false);
  const openQuickReactions = useCallback(() => setQuickReactionsOpen(true), []);

  const me = useMe();
  /* Nothing else subscribes this conversation's realtime topic off `/chat`, and
     `ChatPage` is unmounted while a call is on screen. Without this the rail
     would render the fetched timeline and then sit silent for the whole call. */
  const connection = useChatRealtime(conversationId);
  /* Nor does anything else drain the outbox off `/chat`: `useSendMessage` only
     enqueues. Without this a message typed during a call renders locally and
     never leaves the device until the user next opens Chat. */
  useOutboxFlush(connection);

  const {
    conversation,
    isLoading: loadingConversation,
    resolveRequest,
  } = useConversation(conversationId);
  /* No permalink anchor: `?m=` is a global search param shared with the chat
     page, so honouring it here would park the call's thread on whatever message
     the last deep link pointed at. */
  const timeline = useTimeline(conversationId);
  const pending = useOutbox(conversationId);
  const rows = useTimelineRows(timeline.messages, pending, conversation);
  const actions = useMessageActions(conversationId);
  const extras = useMessageExtras(conversationId, actions, openQuickReactions);
  const typing = useTyping(conversationId);
  const attachments = useAttachments(conversationId);
  const send = useSendMessage(conversationId, me, conversation?.lastSeq ?? 0);
  const sendVoice = useVoiceSend(conversationId, send);

  const failed = useMemo(() => pending.filter((item) => item.state === 'failed'), [pending]);
  /*
   * A MESSAGE REQUEST IS A DECISION, NOT A TEXT BOX — and it has to be the same
   * decision here as on the chat page.
   *
   * `ThreadPane` swaps the composer for `ChatRequestBar` while
   * `self.requestState` is 'pending'; the rail rendered the composer
   * unconditionally, so a guest ringing somebody who has never accepted them
   * got a working-LOOKING composer for a thread they have not been let into.
   * Everything typed in it was accepted locally, queued by the outbox, retried,
   * and refused by the server every time — `canPost` is false while the request
   * is pending — so the only thing the user ever saw was "could not be sent".
   *
   * Same component, same two verbs, same copy: this is a gate, and two gates
   * that disagree are one gate.
   */
  const isRequest = conversation?.self.requestState === 'pending';
  /* The sender's name the way every other surface renders it — the stored
     title, or the counterpart, or the first three members. */
  const view = useMemo(() => viewOf(conversation, me?.id ?? null, me), [conversation, me]);
  const lastSeq = timeline.messages[timeline.messages.length - 1]?.seq ?? 0;
  /* Only safe because the rail is UNMOUNTED whenever the call surface goes
     compact: a `display:none` pane still reports `atBottom`, and would quietly
     clear the unread badge for messages the user never saw. */
  useMarkRead(conversationId, lastSeq, atBottom, conversation?.self.lastReadSeq ?? 0);

  const composer = useThreadComposer({
    messages: timeline.messages,
    meId: me?.id ?? null,
    actions,
    attachments,
    send,
    stopTyping: typing.stop,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <OfflineBanner connection={connection} queued={pending.length} />

      <MessageTimeline
        rows={rows}
        conversation={conversation}
        me={me}
        actions={actions}
        isLoading={timeline.isLoading || loadingConversation}
        hasOlder={timeline.hasOlder}
        isFetchingOlder={timeline.isFetchingOlder}
        loadOlder={timeline.loadOlder}
        onReply={composer.setReplyTo}
        onForward={setForwarding}
        onEdit={composer.setEditing}
        onAtBottomChange={setAtBottom}
        unreadCount={conversation?.self.unreadCount ?? 0}
        extras={extras}
        anchorId={null}
        highlightId={null}
      />

      <TypingRow entries={typing.others} />
      <FailedSendBar
        failed={failed}
        onRetryAll={() => failed.forEach((item) => actions.retry(item.clientMsgId))}
        onDiscardAll={() => failed.forEach((item) => actions.discard(item.clientMsgId))}
      />

      {isRequest ? (
        <ChatRequestBar
          senderName={view.title}
          onAccept={() => resolveRequest('accept')}
          onBlock={() => resolveRequest('block')}
        />
      ) : (
        <div className="shrink-0 p-2">
          <Composer
            conversationId={conversationId}
            attachments={attachments.attachments}
            onAddFiles={attachments.add}
            onRemoveAttachment={attachments.remove}
            replyTo={composer.replyTo}
            editing={composer.editing}
            onCancelContext={composer.cancelContext}
            onSend={composer.submit}
            onSendVoice={sendVoice}
            onTyping={typing.notify}
            onStopTyping={typing.stop}
            onEditLast={composer.editLast}
            /* THE MICROPHONE IS ALREADY IN USE — by this call.
               `useVoiceRecorder` opens its own `getUserMedia({ audio: true })`,
               ignoring the device the call was told to use, and on iOS/Safari
               that second capture can mute or kill the live track. Everything
               else in the composer works normally; a voice note is one tap away
               in Chat, after the call. */
            voiceMessages={false}
          />
        </div>
      )}

      {forwarding === null ? null : (
        <CallForwardDialog
          message={forwarding}
          meId={me?.id ?? null}
          onClose={() => setForwarding(null)}
        />
      )}

      <QuickReactionsDialog open={quickReactionsOpen} onOpenChange={setQuickReactionsOpen} />
    </div>
  );
}
